// web/src/hooks/useBeforeUnload.ts
import { useEffect } from 'react'

/**
 * While `enabled` is true, attaches a `beforeunload` listener so the browser
 * shows its native "Leave site?" confirmation when the user tries to close the
 * tab, reload, or navigate away externally. Browsers ignore custom message
 * text, but the provided string is still set for the legacy `returnValue`
 * contract. Does NOT block in-app (React Router) navigation.
 *
 * The listener is removed as soon as `enabled` flips back to false (or on
 * unmount), so the guard is scoped exactly to the in-flight work.
 */
export const useBeforeUnload = (
  enabled: boolean,
  message = 'Changes you made may not be saved.'
): void => {
  'use memo'
  useEffect(() => {
    if (!enabled) {
      return
    }
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      // Required for the prompt to appear in Chrome/legacy browsers.
      event.returnValue = message
      return message
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [enabled, message])
}
